/**
 * Whether a Stream channel is job-scoped. Every signal must agree (AND):
 * id token `job[-_]<id>`, a job-bounded `data.source`, complete `data.meta`,
 * and a `meta.job_id` that does not contradict the token.
 *
 * An AND, not an OR: any single signal is a trace, not a contract. The channel
 * also carries the backend's own `data.is_job_bound`, deliberately unread: it
 * is absent on every channel minted before AI-2879, so trusting it would fail
 * open where this fails closed.
 *
 * `company_id` is never mirrored onto the channel (`jobMetaStreamKeys`), so it
 * cannot be checked here.
 */
export interface JobBoundedInput {
  id: string;
  data?: unknown;
}

/**
 * Mirrors the backend's `domain.JobBoundSources` — the set that answers what a
 * channel IS, and so the one this display rule follows.
 *
 * Not the wider sets composed from it upstream: `career_fair` and
 * `recommended_candidate` are in the Stream display mirror, and `interview` in
 * the job-scoped lookup, but none of the three is a job-bounded source in this
 * product. See `channel-id.ts` and `start-chat.ts` for the other two mirrors.
 */
const JOB_BOUNDED_SOURCES = new Set(['job_application', 'invite_to_apply']);

/**
 * The job fields the backend mirrors onto the Stream channel for a job-bounded
 * source. `job_location` is omitted: `buildStreamJobMeta` skips empty values,
 * and a job without a location is still a job.
 */
const REQUIRED_META_FIELDS = ['job_id', 'job_title', 'company_name'] as const;

// Bounded by `-` or a string boundary so `job_789` never matches a `job_78`
// lookup. Accepts `-` (canonical ids) and `_` (deterministic ids).
const JOB_ID_TOKEN = /(?:^|-)job[-_](\d+)(?:-|$)/;

export function isJobBoundedSource(source: unknown): boolean {
  return typeof source === 'string' && JOB_BOUNDED_SOURCES.has(source);
}

function readData(data: unknown): { source?: unknown; meta?: unknown } {
  return (data as { source?: unknown; meta?: unknown } | null | undefined) ?? {};
}

function readMeta(data: unknown): Record<string, unknown> {
  const meta = readData(data).meta;
  return typeof meta === 'object' && meta !== null ? (meta as Record<string, unknown>) : {};
}

function nonEmpty(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

/**
 * The job id this channel is bound to, or null when it is not job-bounded.
 * Single source of the decision: `isJobBounded` is this returning a value, so
 * the two can never disagree about a channel.
 */
export function jobIdFromChannel(input: JobBoundedInput): string | null {
  const token = JOB_ID_TOKEN.exec(input.id)?.[1];
  // `\d+` admits "0" and "000"; the create gate requires a positive id.
  if (!token || !/[1-9]/.test(token)) return null;

  if (!isJobBoundedSource(readData(input.data).source)) return null;

  const meta = readMeta(input.data);
  if (!REQUIRED_META_FIELDS.every((field) => nonEmpty(meta[field]))) return null;

  // `job_id` is sometimes MASKED since the id cutover: the backend mirrors
  // `source.meta` as the caller sent it while normalizing the channel id. The
  // codec is server-only and this runs in the browser, so a masked value
  // corroborates nothing and must not veto. A plain one still has to agree.
  const declared = String(meta.job_id).trim();
  if (/^\d+$/.test(declared) && declared !== token) return null;

  // The token, never the declared value: every caller downstream queries
  // Elasticsearch on a row id, and the token is the one of the two that is
  // always one.
  return token;
}

export function isJobBounded(input: JobBoundedInput): boolean {
  return jobIdFromChannel(input) !== null;
}
